import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageForm from "components/pageForm";
import Button from "components/button";
import LoadingButton from "components/button/loading";
import UseContext from "hooks/useContext";
import ApiDataBase from "server";
import TemplateSubNav from "pages/jobPostTemplates/components/TemplateSubNav";
import TemplateIndicator from "pages/jobPostTemplates/components/TemplateIndicator";
import TemplateFormsCreate from "pages/jobPostTemplates/templateFormsCreate";
import { Plus } from "assets/icons";

const CreateOfTemplate = ({ mode = "create" }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const {
        templatePage,
        templateData,
        setTemplateData,
        setTemplatePage,
        handleTemplatePrev,
        handleTemplateNext,
        companyData,
        resetTemplateData,
        refreshJobPostTemplates,
        setRefreshJobPostTemplates,
    } = UseContext();
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(mode === "edit");
    /** Réponse API après création / mise à jour (utilisée par l’étape finale). */
    const [savedJobPostDoc, setSavedJobPostDoc] = useState(null);

    useEffect(() => {
        if (mode === "create") {
            resetTemplateData();
            setSavedJobPostDoc(null);
        }
        // eslint-disable-next-line
    }, [mode]);

    useEffect(() => {
        if (mode !== "edit" || !id) return;
        let cancelled = false;
        setIsLoadingTemplate(true);
        ApiDataBase.getJobPostTemplate(
            { id },
            (template) => {
                if (cancelled) return;
                setTemplateData({
                    name: template.name || "",
                    activity: template.activity || "",
                    qualification: template.qualification || "",
                    tools: template.tools || [],
                    licences: template.licences || [],
                    languages: template.language || [],
                    contact_name: template.contact?.name || "",
                    contact_poste: template.contact?.poste || "",
                    location: template.location || "",
                    other: template.other || "",
                    other_interim: template.other_interim || "",
                    salary:
                        typeof template.salary?.wage_per_hour === "number"
                            ? String(template.salary.wage_per_hour)
                            : "",
                    isIndexedToMinimumWage:
                        !!template.isIndexedToMinimumWage,
                });
                setTemplatePage(1);
                setIsLoadingTemplate(false);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                toast.error("Fiche de poste introuvable");
                navigate("/jobPostTemplates");
            }
        );
        return () => {
            cancelled = true;
        };
    }, [id, mode, navigate, setTemplateData, setTemplatePage]);

    const validateCurrentStep = () => {
        switch (templatePage) {
            case 1:
                if (!templateData.activity || !templateData.qualification) {
                    toast.error("Veuillez sélectionner secteur et qualification");
                    return false;
                }
                if (!templateData.name?.trim()) {
                    toast.error("Veuillez renseigner un nom de modèle");
                    return false;
                }
                return true;
            case 2:
                return true;
            case 3:
                if (
                    !templateData.location ||
                    (typeof templateData.location === "object" &&
                        Object.keys(templateData.location).length === 0)
                ) {
                    toast.error("Veuillez renseigner une adresse");
                    return false;
                }
                if (!templateData.salary) {
                    toast.error("Veuillez renseigner un taux horaire");
                    return false;
                }
                return true;
            case 4:
                if (!templateData.contact_name || !templateData.contact_poste) {
                    toast.error("Veuillez compléter les informations responsable");
                    return false;
                }
                return true;
            case 5:
                return true;
            default:
                return true;
        }
    };

    const handleNextClick = () => {
        if (validateCurrentStep()) handleTemplateNext();
    };

    const buildPayload = () => ({
        name: templateData.name?.trim(),
        title: templateData.qualification,
        activity: templateData.activity,
        qualification: templateData.qualification,
        tools: templateData.tools,
        licences: templateData.licences,
        language: templateData.languages,
        contact: {
            name: templateData.contact_name,
            poste: templateData.contact_poste,
        },
        other: templateData.other,
        other_interim: templateData.other_interim,
        location: templateData.location,
        salary: { wage_per_hour: templateData.salary },
        isIndexedToMinimumWage: !!templateData.isIndexedToMinimumWage,
    });

    const handleSubmit = () => {
        setIsSubmitLoading(true);
        const payload = buildPayload();
        if (mode === "edit") {
            ApiDataBase.patchJobPostTemplate(
                { id, data: payload },
                (data) => {
                    setSavedJobPostDoc(data ?? null);
                    setIsSubmitLoading(false);
                    setRefreshJobPostTemplates(!refreshJobPostTemplates);
                    handleTemplateNext();
                },
                (err) => {
                    console.error(err);
                    setIsSubmitLoading(false);
                    toast.error("Modification impossible");
                }
            );
        } else {
            ApiDataBase.postCreateJobPostTemplate(
                {
                    data: payload,
                    idCompany: companyData?._id,
                },
                (data) => {
                    setSavedJobPostDoc(data ?? null);
                    setIsSubmitLoading(false);
                    setRefreshJobPostTemplates(!refreshJobPostTemplates);
                    handleTemplateNext();
                },
                (err) => {
                    console.error(err);
                    setIsSubmitLoading(false);
                    toast.error("Création impossible");
                }
            );
        }
    };

    const isLastStep = templatePage === 5;
    const isEnd = templatePage === 6;

    return (
        <PageForm>
            <div className="col-span-12 bg-white sticky z-10 top-16">
                <TemplateSubNav />
            </div>
            <div className="col-span-full pb-24">
                <div className="w-full mx-auto lg:w-[800px] px-6 py-8">
                    {isLoadingTemplate ? (
                        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
                    ) : (
                        <TemplateFormsCreate
                            mode={mode}
                            savedJobPostDoc={savedJobPostDoc}
                            editTemplateRouteId={mode === "edit" ? id : undefined}
                        />
                    )}
                </div>
            </div>
            {!isEnd && !isLoadingTemplate && (
                <div className="col-span-full">
                    <div className="fixed bottom-0 left-[80px] sidebar-expanded:left-64 2xl:left-64 right-0 bg-white border-t border-gray-100 z-20">
                        <div className="w-full mx-auto lg:w-[800px] px-6 py-4">
                            <div className="flex justify-between items-center gap-4">
                                <TemplateIndicator formIndex={templatePage} />
                                <div className="flex gap-3 shrink-0">
                                    {templatePage > 1 && (
                                        <Button
                                            size={"LG"}
                                            color={"SECONDARY"}
                                            onClick={handleTemplatePrev}
                                        >
                                            Retour
                                        </Button>
                                    )}
                                    {isLastStep ? (
                                        isSubmitLoading ? (
                                            <LoadingButton size={"LG"}>
                                                {mode === "edit"
                                                    ? "Enregistrement…"
                                                    : "Création…"}
                                            </LoadingButton>
                                        ) : (
                                            <Button
                                                size={"LG"}
                                                onClick={handleSubmit}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {mode === "create" && (
                                                        <Plus
                                                            wh={14}
                                                            color={"#fff"}
                                                        />
                                                    )}
                                                    <div>
                                                        {mode === "edit"
                                                            ? "Enregistrer"
                                                            : "Créer la fiche"}
                                                    </div>
                                                </div>
                                            </Button>
                                        )
                                    ) : (
                                        <Button
                                            size={"LG"}
                                            onClick={handleNextClick}
                                        >
                                            Suivant
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default CreateOfTemplate;
